import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { 
  Shield, 
  Users, 
  UserCheck, 
  Eye, 
  CheckCircle,
  XCircle
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

// Permission definitions for each hierarchy level
const HIERARCHY_LEVELS = [
  {
    key: "superAuthority",
    icon: Shield,
    color: "amber",
    designations: [
      { single: "tahsildar" },
      { single: "ddlr" },
      { single: "adlr" }
    ],
    permissions: {
      viewAllApplications: true,
      viewSubordinateApplications: true,
      viewOwnApplications: true,
      manageOfficials: true,
      assignApplications: true,
      bulkAssign: true,
      exportReports: true,
      viewAuditLogs: true,
      viewAnalytics: true,
      processDeadlineAlerts: true,
      approveReject: true,
      skipWorkflowSteps: true
    }
  },
  {
    key: "supervisor",
    icon: Users,
    color: "blue",
    designations: [
      { group: ["shirestedar", "deputy_tahsildar"], separator: "/" },
      { single: "revenue_inspector" }
    ],
    permissions: {
      viewAllApplications: false,
      viewSubordinateApplications: true,
      viewOwnApplications: true,
      manageOfficials: false,
      assignApplications: false,
      bulkAssign: false,
      exportReports: false,
      viewAuditLogs: false,
      viewAnalytics: true,
      processDeadlineAlerts: false,
      approveReject: false,
      skipWorkflowSteps: false
    }
  },
  {
    key: "fieldOfficer",
    icon: UserCheck,
    color: "emerald",
    designations: [
      { single: "village_accountant" },
      { single: "first_division_clerk" },
      { single: "second_division_clerk" },
      { single: "surveyor" }
    ],
    permissions: {
      viewAllApplications: false,
      viewSubordinateApplications: false,
      viewOwnApplications: true,
      manageOfficials: false,
      assignApplications: false,
      bulkAssign: false,
      exportReports: false,
      viewAuditLogs: false,
      viewAnalytics: true,
      processDeadlineAlerts: false,
      approveReject: false,
      skipWorkflowSteps: false
    }
  }
];

const getColorClasses = (color) => {
  const colors = {
    amber: {
      bg: "bg-amber-100",
      border: "border-amber-300",
      text: "text-amber-800",
      icon: "text-amber-600",
      light: "bg-amber-50"
    },
    blue: {
      bg: "bg-blue-100",
      border: "border-blue-300",
      text: "text-blue-800",
      icon: "text-blue-600",
      light: "bg-blue-50"
    },
    emerald: {
      bg: "bg-emerald-100",
      border: "border-emerald-300",
      text: "text-emerald-800",
      icon: "text-emerald-600",
      light: "bg-emerald-50"
    }
  };
  return colors[color] || colors.emerald;
};

export default function HierarchyHelpModal({ triggerClassName = "" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className={`text-xs text-emerald-600 hover:text-emerald-700 hover:underline font-medium cursor-pointer ${triggerClassName}`}
          data-testid="hierarchy-help-btn"
        >
          {t('hierarchyHelp.linkText')}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="hierarchy-help-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="w-5 h-5 text-emerald-600" />
            {t('hierarchyHelp.title')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {/* Introduction */}
          <p className="text-sm text-slate-600">
            {t('hierarchyHelp.introduction')}
          </p>

          {/* Hierarchy Levels */}
          <div className="space-y-4">
            {HIERARCHY_LEVELS.map((level) => {
              const Icon = level.icon;
              const colors = getColorClasses(level.color);
              
              return (
                <div 
                  key={level.key}
                  className={`rounded-lg border ${colors.border} ${colors.light} p-4`}
                  data-testid={`hierarchy-level-${level.key}`}
                >
                  {/* Level Header */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${colors.bg}`}>
                      <Icon className={`w-5 h-5 ${colors.icon}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-semibold ${colors.text}`}>
                        {t(`hierarchy.${level.key}`)}
                      </h3>
                      <p className="text-xs text-slate-600 mt-0.5">
                        {t(`hierarchyHelp.${level.key}Desc`)}
                      </p>
                    </div>
                  </div>

                  {/* Designations */}
                  <div className="mb-3">
                    <p className="text-xs text-slate-500 mb-1.5">{t('hierarchyHelp.designations')}:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {level.designations.map((designation, idx) => {
                        // Handle grouped designations (e.g., Shirestedar/Deputy Tahsildar)
                        if (designation.group) {
                          const groupText = designation.group
                            .map(d => t(`designation.${d}`))
                            .join(designation.separator || "/");
                          return (
                            <Badge 
                              key={`group-${idx}`} 
                              variant="secondary"
                              className={`text-xs ${colors.bg} ${colors.text} border-0`}
                            >
                              {groupText}
                            </Badge>
                          );
                        }
                        // Handle single designation
                        const designationKey = designation.single || designation;
                        return (
                          <Badge 
                            key={designationKey} 
                            variant="secondary"
                            className={`text-xs ${colors.bg} ${colors.text} border-0`}
                          >
                            {t(`designation.${designationKey}`)}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  {/* Data Scope */}
                  <div className="mb-3 p-2 bg-white/60 rounded border border-slate-200">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-slate-500" />
                      <span className="text-xs font-medium text-slate-700">
                        {t('hierarchyHelp.dataScope')}:
                      </span>
                      <span className="text-xs text-slate-600">
                        {t(`hierarchyHelp.${level.key}Scope`)}
                      </span>
                    </div>
                  </div>

                  {/* Permissions Grid */}
                  <div>
                    <p className="text-xs font-bold text-red-600 mb-2">{t('hierarchyHelp.permissions')}:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {Object.entries(level.permissions).map(([perm, allowed]) => {
                        return (
                          <div 
                            key={perm}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs ${
                              allowed 
                                ? 'bg-emerald-50 text-emerald-700' 
                                : 'bg-slate-50 text-slate-400'
                            }`}
                          >
                            {allowed ? (
                              <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                            ) : (
                              <XCircle className="w-3 h-3 text-slate-300 flex-shrink-0" />
                            )}
                            <span className="truncate">{t(`hierarchyHelp.perm.${perm}`)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700">
              <span className="font-semibold">{t('common.note')}:</span> {t('hierarchyHelp.note')}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
